import React, { forwardRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import SydInput from '../SydInput'
import { nativeSelectStyle, disabledSelectStyle } from '../sydPickerStyles'

function parseValue (val) {
  if (!val) return { date: '', hour: 8, minute: 0, ampm: 'AM' }
  const [datePart, timePart] = val.split('T')
  if (!timePart) return { date: datePart || '', hour: 8, minute: 0, ampm: 'AM' }
  const [hStr, mStr] = timePart.split(':')
  const h24 = parseInt(hStr, 10)
  const m = parseInt(mStr, 10) || 0
  return {
    date: datePart,
    hour: h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24,
    minute: m,
    ampm: h24 >= 12 ? 'PM' : 'AM'
  }
}

function toDatetimeLocalString ({ date, hour, minute, ampm }) {
  if (!date) return ''
  let h24 = hour % 12
  if (ampm === 'PM') h24 += 12
  return `${date}T${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1)

const tzStyle = {
  color: 'var(--color-neutral-600)',
  fontSize: '12px',
  whiteSpace: 'nowrap'
}

const SydCompositeDateTimePicker = forwardRef(function SydCompositeDateTimePicker ({
  value,
  onChange = noop,
  onBlur = noop,
  step = 15,
  disabled,
  timezone
}, ref) {
  const parts = useMemo(() => parseValue(value), [value])

  const minuteValues = useMemo(() => {
    const vals = []
    for (let m = 0; m < 60; m += step) vals.push(m)
    return vals
  }, [step])

  const handlePartChange = useCallback((part, partValue) => {
    const next = { ...parts, [part]: partValue }
    onChange(toDatetimeLocalString(next))
  }, [parts, onChange])

  const disabledStyle = disabled ? disabledSelectStyle : undefined

  return (
    <div ref={ref} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
      <div style={{ flex: 1 }}>
        <SydInput
          type='date'
          value={parts.date}
          onChange={(e) => handlePartChange('date', e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          minWidth='0'
        />
      </div>
      <select
        value={parts.hour}
        onChange={(e) => handlePartChange('hour', Number(e.target.value))}
        disabled={disabled}
        style={{ ...nativeSelectStyle, ...disabledStyle }}
      >
        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span style={{ fontWeight: 600, lineHeight: 1 }}>:</span>
      <select
        value={parts.minute}
        onChange={(e) => handlePartChange('minute', Number(e.target.value))}
        disabled={disabled}
        style={{ ...nativeSelectStyle, ...disabledStyle }}
      >
        {minuteValues.map(m => (
          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
        ))}
      </select>
      <select
        value={parts.ampm}
        onChange={(e) => handlePartChange('ampm', e.target.value)}
        disabled={disabled}
        style={{ ...nativeSelectStyle, ...disabledStyle }}
      >
        <option value='AM'>AM</option>
        <option value='PM'>PM</option>
      </select>
      {timezone ? <span style={tzStyle}>{timezone}</span> : null}
    </div>
  )
})

SydCompositeDateTimePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  step: PropTypes.number,
  disabled: PropTypes.bool,
  timezone: PropTypes.string
}

export default SydCompositeDateTimePicker
